package dev.lingvo.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.DeviceApprovalDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.SessionState
import dev.lingvo.site.api.rememberSession
import dev.lingvo.site.components.marketing.AuthPageScaffold
import dev.lingvo.site.components.marketing.PublicTextLink
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSpinner
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * Approving a terminal.
 *
 * `lingvo login --token lgo_…` puts a credential in shell history and in `ps` output. This is the
 * replacement: the terminal prints a code, this page confirms what it is asking for, and the token
 * travels back over the terminal's own poll.
 *
 * **No secret is rendered here at all.** On approval the page says "return to your terminal" and
 * nothing else — that is the entire point of the flow, and showing the token "for convenience" would
 * undo it.
 *
 * **This screen is the only defence against consent phishing.** The flow is shaped exactly like the
 * attack: somebody starts a pairing on their machine and sends you the code. So the page names the
 * machine, the client and the address that asked, names the account whose access is about to be
 * delegated, and gives Approve and Deny the same visual weight — a big primary Approve beside a
 * quiet Deny is a design that collects approvals.
 *
 * **The chrome is load-bearing, not decoration.** This page used to render as a bare `Column`: no
 * wordmark, no title, no nav, no link anywhere. It is the one page in the product most dependent on
 * looking legitimate, and it was the only one that looked like nothing. [AuthPageScaffold] is what
 * `/invite`, `/verify-email`, `/auth/callback` and `/checkout` all use for the same reason, and it
 * also supplies the exit this page had none of.
 */
@Page("/device")
@Composable
fun DevicePage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val session = rememberSession()
    var code by remember { mutableStateOf("") }
    var approval by remember { mutableStateOf<DeviceApprovalDto?>(null) }
    var message by remember { mutableStateOf<String?>(null) }
    var outcome by remember { mutableStateOf<DeviceOutcome?>(null) }
    var busy by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        document.title = tr("device.connect_a_device_lingvo", "Connect a device — Lingvo")
    }

    // **Every approval route requires a session**, so a signed-out visitor following their
    // terminal's URL used to type the code, press Continue, and be shown the API's default 401 —
    // "Authentication is required." — on a page with nowhere to go. `Loading` is separated from
    // `LoggedOut` for the reason `Checkout.kt` separates them: a stored token being checked is not
    // the same fact as no token, and drawing "sign in first" during the round trip tells a
    // signed-in person the opposite of the truth.
    val signedOut = session is SessionState.LoggedOut
    val checkingSession = session is SessionState.Loading

    AuthPageScaffold(
        title = if (signedOut) {
            t("device.sign_in_to_approve_this_device", "Sign in to approve this device")
        } else {
            t("device.connect_a_device", "Connect a device")
        },
        subtitle = when {
            checkingSession -> t("device.checking_your_session", "Checking your session.")
            signedOut -> t(
                "device.approving_a_pairing_hands_a_device",
                "Approving a pairing hands a device your own access, so Lingvo has to know who " +
                    "you are before it will ask you.",
            )
            outcome != null -> t(
                "device.the_decision_is_recorded_nothing_else",
                "The decision is recorded. Nothing else is needed here.",
            )
            approval != null -> t(
                "device.check_every_line_below_before_you_decide",
                "Check every line below before you decide.",
            )
            else -> t("device.enter_the_code_your_terminal_is", "Enter the code your terminal is showing.")
        },
        alternatePrompt = t("device.not_expecting_this", "Not expecting this?"),
        alternateAction = t("device.go_to_lingvo", "Go to Lingvo"),
        alternatePath = "/",
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            when {
                checkingSession -> LSpinner(label = t("device.checking_your_session_label", "Checking your session"))

                signedOut -> SignedOutCard(
                    onLogIn = { ctx.router.tryRoutingTo("/login") },
                    onSignUp = { ctx.router.tryRoutingTo("/signup") },
                )

                outcome != null -> OutcomeCard(outcome!!)

                approval == null -> LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LTextField(
                            code,
                            { code = it },
                            Modifier.fillMaxWidth(),
                            label = t("device.device_code", "Device code"),
                            // A format specimen, not copy. The server mints eight characters
                            // chunked 4-4 with a hyphen, so a translated placeholder would tell
                            // somebody to expect a shape the product never produces — the same
                            // reason the script specimens are marked. It had a catalogue key.
                            placeholder = "WK4Q-7TBN", // i18n-ignore
                            enabled = !busy,
                        )
                        message?.let {
                            LAlert(
                                t("device.that_didnt_work", "That didn't work"),
                                it,
                                variant = LAlertVariant.WARNING,
                                // It arrives in answer to Continue, after paint.
                                live = true,
                            )
                        }
                        LButton(
                            t("device.continue", "Continue"),
                            {
                                scope.launch {
                                    busy = true
                                    message = null
                                    try {
                                        approval = ApiClient.deviceApproval(code)
                                    } catch (cancelled: CancellationException) {
                                        throw cancelled
                                    } catch (failure: Throwable) {
                                        message = failure.message?.takeIf(String::isNotBlank)
                                            ?: tr(
                                                "device.that_code_isnt_valid_codes_expire",
                                                "That code isn't valid. Codes expire five minutes " +
                                                    "after your terminal printed them.",
                                            )
                                    } finally {
                                        busy = false
                                    }
                                }
                            },
                            enabled = code.isNotBlank() && !busy,
                            loading = busy,
                        )
                    }
                }

                else -> ApprovalCard(
                    approval = approval!!,
                    busy = busy,
                    message = message,
                    onApprove = {
                        scope.launch {
                            busy = true
                            message = null
                            try {
                                ApiClient.approveDevice(code)
                                outcome = DeviceOutcome.APPROVED
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                message = failure.message?.takeIf(String::isNotBlank)
                                    ?: tr(
                                        "device.couldnt_approve_this_device_check_your",
                                        "Couldn't approve this device. Check your connection and " +
                                            "try again.",
                                    )
                            } finally {
                                busy = false
                            }
                        }
                    },
                    onDeny = {
                        scope.launch {
                            busy = true
                            message = null
                            // **A refusal that never landed must not be reported as a refusal.**
                            // This discarded the request's result and set "Denied. Nothing was
                            // connected." unconditionally, so a dropped connection told somebody
                            // they had refused a pairing that was still live and still approvable
                            // for the rest of its five minutes. On the one screen whose job is
                            // refusing a pairing, that is the worst direction to be wrong in.
                            try {
                                ApiClient.denyDevice(code)
                                outcome = DeviceOutcome.DENIED
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                message = failure.message?.takeIf(String::isNotBlank)
                                    ?: tr(
                                        "device.couldnt_record_the_refusal_so_the",
                                        "Couldn't record the refusal, so the request is still " +
                                            "live. Try Deny again, or stop the command in the " +
                                            "terminal that asked.",
                                    )
                            } finally {
                                busy = false
                            }
                        }
                    },
                )
            }
        }
    }
}

/**
 * What a signed-out visitor gets instead of a 401 they cannot act on.
 *
 * Neither `/login` nor `/signup` reads a return path yet, so this does not promise one. It says
 * where to come back to and how long the code lasts, which is the whole of what a person needs to
 * finish — and it is a great deal more than the address bar this page used to leave them with.
 */
@Composable
private fun SignedOutCard(onLogIn: () -> Unit, onSignUp: () -> Unit) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LMuted(
                t(
                    "device.sign_in_then_come_back_to",
                    "Sign in, then come back to this page and enter the code. Your terminal's " +
                        "code lasts five minutes from the moment it printed — if it has run out, " +
                        "run lingvo login --device again for a fresh one.",
                ),
            )
            LButton(
                t("device.log_in", "Log in"),
                onLogIn,
                modifier = Modifier.fillMaxWidth(),
            )
            LButton(
                t("device.create_an_account", "Create an account"),
                onSignUp,
                modifier = Modifier.fillMaxWidth(),
                variant = LButtonVariant.SECONDARY,
            )
        }
    }
}

/**
 * The decision, after it has been recorded.
 *
 * `live = true` because this is the definition of a post-action notice, and it used to be a bold
 * `Span` inside a card — the single most important sentence on the page, announced to nobody.
 */
@Composable
private fun OutcomeCard(outcome: DeviceOutcome) {
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            when (outcome) {
                DeviceOutcome.APPROVED -> LAlert(
                    t("device.connected", "Connected"),
                    t(
                        "device.return_to_your_terminal_it_already",
                        "Return to your terminal — it picks the credential up within a few " +
                            "seconds. Nothing is shown here and nothing needs copying.",
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.SUCCESS,
                    live = true,
                )

                DeviceOutcome.DENIED -> LAlert(
                    t("device.denied", "Denied"),
                    t(
                        "device.nothing_was_connected_the_terminal_that",
                        "Nothing was connected. The terminal that asked will stop waiting.",
                    ),
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.INFO,
                    live = true,
                )
            }
            if (outcome == DeviceOutcome.APPROVED) {
                LMuted(
                    t(
                        "device.you_can_close_this_page_the",
                        "You can close this page. The connected device appears under Settings → " +
                            "API tokens, and you can revoke it there at any time.",
                    ),
                )
                // Named and then not linked, which is the shape of a credential nobody can find —
                // and a credential nobody can find is one nobody revokes.
                PublicTextLink(
                    "/app/settings/tokens",
                    t("device.settings_api_tokens", "Settings → API tokens"),
                )
            }
        }
    }
}

@Composable
private fun ApprovalCard(
    approval: DeviceApprovalDto,
    busy: Boolean,
    message: String?,
    onApprove: () -> Unit,
    onDeny: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            // The consent question, at the size it always was — an `H2` rather than a bold `Span`.
            // It was sized as a heading and exposed as body text, so a screen reader's heading list
            // for a consent screen held one entry: the page title. The pixels do not move.
            LSectionTitle(
                t("device.a_device_is_asking_to_connect", "A device is asking to connect to your account"),
                Modifier.fontSize(TypeScale.HeadingSmall),
            )

            // Whose access is being handed over. The page named the machine, the client and the
            // address, and never named the one fact that separates "my terminal" from "a
            // stranger's code". It is ours rather than the client's, so it is stated apart from
            // the self-reported block below.
            Session.user?.let { user ->
                Fact(t("device.approving_as", "Approving as"), user.email)
            }

            // Facts a person can check against what is in front of them. If none of this looks
            // familiar, that is the signal — and it is the only signal there is.
            Column(Modifier.gap(Space.XS)) {
                // Self-reported by the client, so rendered as plain text and never as markup.
                Fact(t("device.machine", "Machine"), approval.machineLabel ?: "not stated")
                Fact(t("device.client", "Client"), approval.clientName ?: "not stated")
                Fact(t("device.from", "From"), approval.requestedIp ?: "unknown address")
            }

            Column(Modifier.gap(Space.XS)) {
                // An `H2`, not a bold `Span`. It labels the list beneath it, and a heading a
                // screen reader cannot find is not a label — this page had exactly one heading in
                // its outline, on a consent screen.
                LSectionTitle(
                    t("device.it_will_be_able_to", "It will be able to"),
                    Modifier.fontSize(TypeScale.BodySmall).attr("id", SCOPES_HEADING_ID),
                )
                // A real list, so the count is announced. `role="list"` because `list-style: none`
                // is enough for WebKit to drop list semantics entirely.
                Ul(
                    Modifier
                        .fillMaxWidth()
                        .gap(Space.XS)
                        .margin(0.px)
                        .padding(0.px)
                        .styleModifier {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("list-style", "none")
                        }
                        .attr("role", "list")
                        .attr("aria-labelledby", SCOPES_HEADING_ID)
                        .toAttrs(),
                ) {
                    approval.requestedScopes.forEach { requested ->
                        val granted = requested in approval.grantedScopes
                        Li(
                            Modifier
                                .fontSize(TypeScale.Small)
                                .color(if (granted) palette.textPrimary else palette.textMuted)
                                // Struck through rather than hidden: silently granting less is how
                                // a CI job breaks a week later for no visible reason.
                                .then(
                                    if (granted) Modifier
                                    else Modifier.textDecorationLine(TextDecorationLine.LineThrough),
                                )
                                .toAttrs(),
                        ) {
                            Text(tf("device.scope_and_description", "{scope} — {description}", "scope" to requested.id, "description" to requested.description))
                            if (!granted) {
                                // A rule through the text is paint. Without this a screen reader
                                // reads a stripped permission exactly like a granted one, on the
                                // screen whose entire job is showing what is being handed over.
                                Span(LVisuallyHiddenStyle.toModifier().toAttrs()) {
                                    Text(t("device.not_granted", " — not granted"))
                                }
                            }
                        }
                    }
                }
                if (approval.grantedScopes.size < approval.requestedScopes.size) {
                    LMuted(
                        t("device.the_struck_through_permissions_are_above_2", "The struck-through permissions are above what your role allows, so they " +
                            "will not be granted. The device will work with the rest."),
                    )
                }
            }

            if (approval.grantedScopes.isEmpty()) {
                LAlert(
                    t("device.nothing_would_be_granted", "Nothing would be granted"),
                    t(
                        "device.your_role_cannot_delegate_any_of",
                        "Your role cannot delegate any of the permissions this device asked for.",
                    ),
                    variant = LAlertVariant.WARNING,
                )
            }

            // **The failure had nowhere to render.** `message` was only drawn in the code-entry
            // branch, which is off screen by the time this card exists — so a failed Approve
            // stopped the spinner and changed nothing else, while the terminal went on waiting.
            message?.let {
                LAlert(
                    t("device.that_didnt_work_card", "That didn't work"),
                    it,
                    modifier = Modifier.fillMaxWidth(),
                    variant = LAlertVariant.DANGER,
                    live = true,
                )
            }

            // Equal weight, deliberately. A big primary Approve beside a quiet Deny is a design that
            // collects approvals rather than decisions.
            Row(Modifier.gap(Space.SM)) {
                LButton(t("device.approve", "Approve"), onApprove, enabled = !busy, variant = LButtonVariant.SECONDARY)
                LButton(t("device.deny", "Deny"), onDeny, enabled = !busy, variant = LButtonVariant.SECONDARY)
            }
            // The deadline was on the wire and never on the screen. It is what makes "decide now"
            // the right instruction rather than "come back to this later".
            LMuted(
                tPlural(
                    "device.this_request_expires_in_about",
                    ((approval.expiresInSeconds + 59) / 60).coerceAtLeast(1),
                    "This request expires in about {count} minute. After that the terminal has to ask again.",
                    "This request expires in about {count} minutes. After that the terminal has to ask again.",
                ),
            )
            LMuted(t("device.if_you_were_not_expecting_this", "If you were not expecting this, deny it. Nothing is connected until you approve."))
        }
    }
}

@Composable
private fun Fact(label: String, value: String) {
    Row(Modifier.gap(Space.SM)) {
        Span(
            Modifier.fontSize(TypeScale.Small).color(ColorMode.current.toSitePalette().textSecondary).toAttrs(),
        ) {
            Text(tf("device.field_label", "{label}:", "label" to label))
        }
        Span(Modifier.monoFont().fontSize(TypeScale.Small).toAttrs()) { Text(value) }
    }
}

/**
 * The decision, held as a decision rather than as a sentence.
 *
 * The English used to live in the state variable, which put the page's two most consequential
 * strings outside the catalogue and made "denied" indistinguishable from "approved" to everything
 * except a string comparison.
 */
private enum class DeviceOutcome { APPROVED, DENIED }

/** Ties the scope list to the heading that names it. One approval card renders at a time. */
private const val SCOPES_HEADING_ID = "device-scopes-heading"
